import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

type ActionState = 'pending' | 'accepted' | 'overridden' | 'dismissed';

interface FlaggedCall {
  id: string;
  ani?: string | null;
  buyerId?: string | null;
  callerState?: string | null;
  totalDurationSeconds?: number | null;
  fraudEvent: {
    id: string;
    type: string;
    severity?: string | null;
    details?: Record<string, unknown> | null;
  };
  classification?: {
    tier?: string | null;
    primaryIntent?: string | null;
  } | null;
}

function formatDuration(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function maskAni(ani: string | null | undefined): string {
  if (!ani) return '—';
  if (ani.length > 4) return `(***) ***-${ani.slice(-4)}`;
  return ani;
}

function severityClass(severity: string | null | undefined) {
  switch (severity) {
    case 'high':   return 'bg-red-900/40 text-red-400 border-red-800/50';
    case 'medium': return 'bg-orange-900/40 text-orange-400 border-orange-800/50';
    default:       return 'bg-yellow-900/40 text-yellow-400 border-yellow-800/50';
  }
}

function SkeletonCard() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 animate-pulse">
      <div className="flex gap-3">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-slate-800 rounded w-3/4" />
          <div className="h-3 bg-slate-800 rounded w-1/2" />
          <div className="h-3 bg-slate-800 rounded w-full" />
        </div>
        <div className="w-24 h-8 bg-slate-800 rounded-lg" />
      </div>
    </div>
  );
}

export function FlaggedCalls() {
  const [calls, setCalls] = useState<FlaggedCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState<Record<string, ActionState>>({});

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.calls.flagged.query({ tenantId: user.tenantId })
      .then(data => setCalls(data as FlaggedCall[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function act(id: string, action: ActionState) {
    setActions(prev => ({ ...prev, [id]: action }));
  }

  const pending = calls.filter(c => !actions[c.id] || actions[c.id] === 'pending');
  const resolved = calls.filter(c => actions[c.id] && actions[c.id] !== 'pending');

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 w-32 h-16 animate-pulse" />
          ))}
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex gap-3 flex-wrap">
        <div className="bg-rose-900/20 border border-rose-800/40 rounded-xl px-4 py-3 flex items-center gap-2">
          <span className="text-2xl font-bold text-rose-400">{pending.length}</span>
          <span className="text-xs text-rose-300">Pending Review</span>
        </div>
        <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-xl px-4 py-3 flex items-center gap-2">
          <span className="text-2xl font-bold text-emerald-400">{resolved.length}</span>
          <span className="text-xs text-emerald-300">Resolved</span>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-2">
          <span className="text-2xl font-bold text-slate-200">{calls.length}</span>
          <span className="text-xs text-slate-400">Total Flagged</span>
        </div>
      </div>

      {calls.length === 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center">
          <p className="text-3xl mb-2">✅</p>
          <p className="text-slate-300 font-medium text-sm">No flagged calls</p>
          <p className="text-slate-500 text-xs mt-1">All clear — no calls pending fraud review.</p>
        </div>
      )}

      {pending.length === 0 && calls.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center">
          <p className="text-3xl mb-2">✅</p>
          <p className="text-slate-300 font-medium text-sm">All flags resolved</p>
          <p className="text-slate-500 text-xs mt-1">No calls pending review.</p>
        </div>
      )}

      {/* Flagged cards */}
      <div className="space-y-3">
        {calls.map(call => {
          const state = actions[call.id] || 'pending';
          const isResolved = state !== 'pending';
          const severity = call.fraudEvent.severity;
          const sev = severity ?? 'medium';

          return (
            <div
              key={call.id}
              className={`bg-slate-900 border rounded-xl p-4 transition-opacity ${
                isResolved ? 'opacity-50 border-slate-800' : 'border-rose-800/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                {/* Call info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-mono text-sm font-medium text-slate-200">{maskAni(call.ani)}</span>
                    <span className="text-slate-500 text-xs">·</span>
                    <span className="text-xs text-slate-400">{call.callerState ?? '—'}</span>
                    {call.totalDurationSeconds != null && (
                      <>
                        <span className="text-slate-500 text-xs">·</span>
                        <span className="text-xs text-slate-400">{formatDuration(call.totalDurationSeconds)}</span>
                      </>
                    )}
                  </div>

                  {/* Fraud type badge */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${severityClass(sev)}`}>
                      {call.fraudEvent.type.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${severityClass(sev)}`}>
                      {sev} severity
                    </span>
                  </div>

                  {call.fraudEvent.details && typeof call.fraudEvent.details === 'object' && (
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {JSON.stringify(call.fraudEvent.details).slice(0, 120)}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isResolved ? (
                    <span className={`text-xs px-3 py-1.5 rounded-lg font-medium
                      ${state === 'accepted' ? 'bg-emerald-900/40 text-emerald-400' :
                        state === 'overridden' ? 'bg-blue-900/40 text-blue-400' :
                        'bg-slate-700 text-slate-400'}`}>
                      {state === 'accepted' ? '✓ Accepted' :
                       state === 'overridden' ? '↺ Overridden' :
                       '✕ Dismissed'}
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => act(call.id, 'accepted')}
                        className="px-3 py-1.5 text-xs rounded-lg bg-emerald-900/40 text-emerald-400 hover:bg-emerald-900/60 border border-emerald-800/50 transition-colors font-medium"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => act(call.id, 'overridden')}
                        className="px-3 py-1.5 text-xs rounded-lg bg-blue-900/40 text-blue-400 hover:bg-blue-900/60 border border-blue-800/50 transition-colors font-medium"
                      >
                        Override
                      </button>
                      <button
                        onClick={() => act(call.id, 'dismissed')}
                        className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700 transition-colors font-medium"
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
